import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, money, cld, CATEGORY_LABEL } from "@/lib/api";

const Row = ({ label, value, testid, strong }) => (
  <div className={`flex justify-between ${strong ? "pt-4 text-base" : "text-sm text-[#3A3733]"}`}>
    <span>{label}</span>
    <span data-testid={testid}>{value}</span>
  </div>
);

const CouponField = ({ coupon, setCoupon }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code });
      setCoupon(data.code);
      toast.success(`Code ${data.code} applied`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (coupon) {
    return (
      <div className="flex items-center justify-between text-sm" data-testid="cart-coupon-applied">
        <span>Code <strong>{coupon}</strong> applied</span>
        <button type="button" className="text-xs underline underline-offset-4" onClick={() => setCoupon(null)} data-testid="cart-coupon-remove">Remove</button>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-4">
      <label className="flex-1">
        <span className="field-label">Discount code</span>
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="field uppercase" data-testid="cart-coupon-input" />
      </label>
      <button type="button" onClick={apply} disabled={busy} className="pill border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFBF7]" data-testid="cart-coupon-apply">Apply</button>
    </div>
  );
};

export const OrderSummary = ({ quote, error, coupon, setCoupon, busy }) => (
  <div className="bg-[#F7F4EE] p-8 sm:p-10 lg:sticky lg:top-28" data-testid="order-summary">
    <p className="eyebrow">Order summary</p>
    {error && <p className="mt-6 text-sm text-[#8B2E24]" data-testid="checkout-quote-error">{error}</p>}
    <div className="mt-8 space-y-6">
      {quote?.items.map((i) => (
        <div key={`${i.productId}-${i.variantIndex}`} className="flex gap-4">
          <img src={cld(i.image, "w_160,q_auto,f_auto")} alt={i.title} className="h-20 w-16 object-cover" />
          <div className="flex-1">
            <p className="font-display text-lg leading-tight">{i.title}</p>
            <p className="text-xs text-[#5A626A]">{CATEGORY_LABEL[i.category]}{i.isOriginal ? "" : ` · ${i.variantLabel} × ${i.quantity}`}</p>
          </div>
          <span className="text-sm">{money(i.price * i.quantity)}</span>
        </div>
      ))}
    </div>
    <div className="mt-8 border-t border-[#E5E0D8] pt-8">
      <CouponField coupon={coupon} setCoupon={setCoupon} />
    </div>
    {quote && (
      <div className="mt-8 space-y-3 border-t border-[#E5E0D8] pt-6">
        <Row label="Subtotal" value={money(quote.subtotal)} testid="summary-subtotal" />
        {quote.discount > 0 && <Row label={`Discount (${quote.couponCode})`} value={`−${money(quote.discount)}`} testid="summary-discount" />}
        <Row label={`Shipping to ${quote.countryName}`} value={quote.shippingFee ? money(quote.shippingFee) : "Free"} testid="summary-shipping" />
        {quote.insuranceFee > 0 && <Row label={`Transit insurance (${Math.round(quote.insuranceRate * 100)}% of declared value)`} value={money(quote.insuranceFee)} testid="summary-insurance" />}
        <div className="border-t border-[#E5E0D8]">
          <Row label="Total (AUD)" value={money(quote.totalAmount)} testid="summary-total" strong />
        </div>
      </div>
    )}
    <button type="submit" disabled={busy || !quote || !!error} className="btn-primary mt-10 w-full" data-testid="checkout-submit-button">
      {busy ? "Opening secure checkout…" : "Continue to payment"}
    </button>
  </div>
);
